/**
 * Resolves the best-matching localized entry from an array of translations/languageData.
 *
 * Comparisons are case-insensitive — locale strings from ZLWrapi are not normalized
 * and may arrive in any casing (e.g. 'en-au', 'en-AU', 'en-US').
 *
 * Language-prefix matching handles mismatches between 5-char codes ('en-US') and
 * 2-char codes ('en') that ZLWrapi may return — 'en' is treated as compatible with
 * 'en-US', 'en-AU', etc.
 *
 * Priority:
 *  1. Exact case-insensitive match on currentLocale
 *  2. Exact case-insensitive match on siteDefaultLocale
 *  3. Language-prefix match on currentLocale  (e.g. 'en' matches 'en-US' or vice versa)
 *  4. Language-prefix match on siteDefaultLocale
 *  5. First entry with a non-empty name
 *  6. undefined (caller should fall back to alias or similar)
 */
export function resolveLocalizedEntry<T extends { locale: string; name: string }>(
    items: T[],
    currentLocale: string,
    siteDefaultLocale?: string
): T | undefined {
    const locale = currentLocale.toLowerCase();
    const fallback = siteDefaultLocale?.toLowerCase();
    const localeLanguage = locale.split('-')[0];
    const fallbackLanguage = fallback?.split('-')[0];

    const byLocale = (target: string) => (item: T) => item.locale.toLowerCase() === target;
    const byLanguagePrefix = (lang: string) => (item: T) => item.locale.toLowerCase().split('-')[0] === lang;

    // 1. Exact match on user's current locale (e.g. 'en-US')
    const exactMatch = items.find(byLocale(locale));
    if (exactMatch) return exactMatch;

    // 2. Exact match on site's default locale (e.g. 'de-DE')
    const fallbackExactMatch = fallback ? items.find(byLocale(fallback)) : undefined;
    if (fallbackExactMatch) return fallbackExactMatch;

    // 3. Language-prefix match on current locale (e.g. 'en' matches 'en-AU')
    const prefixMatch = items.find(byLanguagePrefix(localeLanguage));
    if (prefixMatch) return prefixMatch;

    // 4. Language-prefix match on site's default locale
    const fallbackPrefixMatch = fallbackLanguage ? items.find(byLanguagePrefix(fallbackLanguage)) : undefined;
    if (fallbackPrefixMatch) return fallbackPrefixMatch;

    // 5. Any entry with a non-empty name
    // Assumes that 'name' is the minimum viable field — an entry without a name is considered
    // incomplete and not worth returning, even if it has a description.
    return items.find((item) => Boolean(item.name));
}
